import { useCallback } from "react";
import {
  authApi,
  type SignupCheckResponse,
  type SetupAccountResponse,
  type LoginUser,
  type SignupCheckIntent,
} from "../services/api";
import { useApiRequest } from "./useApiRequest";

export type NextStep =
  | "verify_email"
  | "setup_account"
  | "enter_password"
  | "oauth_login"
  | "pending_approval"
  /** Only for `intent: "login"`: nothing is registered under this email. */
  | "no_account";

export interface CheckEmailResult {
  email: string;
  next_step: NextStep;
}

export interface LoginResult {
  access_token: string;
  token_type: string;
  user: LoginUser;
}

/** Same contract as `checkEmail` without toggling hook loading state (e.g. background tab sync). */
export async function fetchSignupEmailCheck(
  email: string,
  intent?: SignupCheckIntent,
): Promise<CheckEmailResult> {
  // Sign-up callers send just the email, so the backend's default ("signup") applies.
  const result = await authApi.checkSignupEmail(intent ? { email, intent } : { email });
  return result as SignupCheckResponse;
}

/** Same contract as `verifyEmail` without toggling hook loading state (e.g. email-link landing page). */
export async function fetchVerifyEmailToken(
  token: string,
): Promise<{ email: string }> {
  return authApi.verifyEmail({ token });
}

export const useAuthApi = () => {
  const { run, isLoading, error, setError } = useApiRequest();

  const checkEmail = useCallback(
    async (email: string, intent?: SignupCheckIntent): Promise<CheckEmailResult> => {
      return run(() => fetchSignupEmailCheck(email, intent));
    },
    [run],
  );

  const sendVerification = useCallback(
    async (email: string): Promise<void> => {
      return run(async () => {
        await authApi.sendVerification({ email });
      });
    },
    [run],
  );

  const setupAccount = useCallback(
    async (
      email: string,
      firstName: string,
      lastName: string,
      password: string,
    ): Promise<SetupAccountResponse> => {
      return run(() =>
        authApi.setupAccount({
          email,
          first_name: firstName,
          last_name: lastName,
          full_name: `${firstName} ${lastName}`.trim(),
          password,
        }),
      );
    },
    [run],
  );

  const verifyEmail = useCallback(
    async (token: string): Promise<{ email: string }> => {
      return run(() => fetchVerifyEmailToken(token));
    },
    [run],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      return run(() => authApi.login({ email, password }));
    },
    [run],
  );

  const forgotPassword = useCallback(
    async (email: string): Promise<void> => {
      return run(async () => {
        await authApi.forgotPassword({ email });
      });
    },
    [run],
  );

  const resetPassword = useCallback(
    async (token: string, newPassword: string): Promise<void> => {
      return run(async () => {
        await authApi.resetPassword({ token, new_password: newPassword });
      });
    },
    [run],
  );

  const logout = useCallback(
    async (_token: string): Promise<void> => {
      await authApi.logout();
    },
    [],
  );

  const authorizedFetch = useCallback(
    (token: string) =>
      (input: RequestInfo, init: RequestInit = {}) =>
        fetch(input, {
          ...init,
          headers: {
            ...(init.headers || {}),
            Authorization: `Bearer ${token}`,
          },
        }),
    [],
  );

  return {
    checkEmail,
    sendVerification,
    setupAccount,
    verifyEmail,
    login,
    forgotPassword,
    resetPassword,
    logout,
    authorizedFetch,
    isLoading,
    error,
    setError,
  };
};
